import React, { FormEvent, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { customer_feedback } from "../../API/api";
import customFetch from "../../API/CustomFetch";
import showToastMessage from "../../Toastify/Index";
import Navbar from "../customer-consent/components/Navbar";

const ratingOptions = [1, 2, 3, 4, 5];

const FeedbackPage = () => {
  const { LeadId } = useParams();
  const location = useLocation();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const leadId =
    LeadId ||
    searchParams.get("LeadId") ||
    searchParams.get("leadId") ||
    searchParams.get("lead") ||
    "";

  const [rating, setRating] = useState("");
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState<{ rating?: string; comment?: string }>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const validateForm = () => {
    const nextErrors: { rating?: string; comment?: string } = {};

    if (!rating) {
      nextErrors.rating = "Rating is required.";
    }

    if (!comment.trim()) {
      nextErrors.comment = "Comment is required.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccessMessage("");

    if (!leadId) {
      showToastMessage(false, "LeadId is missing in the URL.");
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await customFetch(
        customer_feedback(leadId),
        JSON.stringify({
          rating: Number(rating),
          comment: comment.trim(),
        }),
        { method: "POST" },
      );

      if (response?.SUCCESS) {
        setSuccessMessage("Feedback submitted successfully.");
        setRating("");
        setComment("");
        setErrors({});
      } else {
        showToastMessage(
          false,
          response?.MESSAGE || "Failed to submit feedback",
        );
      }
    } catch {
      showToastMessage(false, "Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F3F5] px-4 py-8">
      <Navbar />
      <div className="mx-auto w-full max-w-xl rounded-2xl bg-white p-6 shadow-sm md:p-8">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-[#1A1A1A]">
            Share Your Feedback
          </h1>
          <p className="mt-2 text-sm text-[#666666]">
            Please rate your experience and leave a comment.
          </p>
        </div>

        {successMessage ? (
          <div className="mt-4 rounded-lg bg-[#E8F8EA] px-4 py-3 text-sm font-medium text-[#0C6B16]">
            {successMessage}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-[#1A1A1A]">
                Rating
              </label>
              <div className="flex items-center gap-2">
                {ratingOptions.map((option) => {
                  const isActive = Number(rating) >= option;

                  return (
                    <button
                      key={option}
                      type="button"
                      aria-label={`Rate ${option} star${option > 1 ? "s" : ""}`}
                      onClick={() => {
                        setRating(String(option));
                        setErrors((prev) => ({ ...prev, rating: undefined }));
                      }}
                      className="text-3xl leading-none transition-transform hover:scale-110"
                    >
                      <span
                        className={
                          isActive ? "text-[#F5B301]" : "text-[#D0D5DD]"
                        }
                      >
                        {"\u2605"}
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.rating && (
                <p className="mt-1 text-sm text-red-600">{errors.rating}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="comment"
                className="mb-2 block text-sm font-medium text-[#1A1A1A]"
              >
                Comment *
              </label>
              <textarea
                id="comment"
                rows={5}
                value={comment}
                onChange={(event) => {
                  setComment(event.target.value);
                  setErrors((prev) => ({ ...prev, comment: undefined }));
                }}
                placeholder="Write your feedback here"
                className="w-full rounded-lg border border-[#D0D5DD] px-4 py-3 text-sm outline-none transition focus:border-[#00AA13]"
              />
              {errors.comment && (
                <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-[#00AA13] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#008F11] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackPage;
